"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys, type QueryFilters } from "@/lib/query-keys";

export const usePlatformUsersList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.usersList(filters),
    queryFn: () => api.platform.listUsers(filters as any),
    placeholderData: keepPreviousData,
  });
};

/**
 * Tenant users (CompanyUser) listed across all organizations.
 * Used by the platform's /admin/tenants/users page.
 */
export const usePlatformTenantUsersList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.tenantUsersList(filters),
    queryFn: () => api.platform.listTenantUsers(filters as any),
    placeholderData: keepPreviousData,
  });
};

export const usePlatformUser = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.user(id || ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) return null;
      const res = await api.platform.getUserById(id);
      return res?.data ?? null;
    },
  });
};

export const useCreatePlatformUser = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.platform.createUser>[0]) =>
      api.platform.createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.users() });
    },
  });
};

export const useUpdatePlatformUser = (id: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.platform.updateUser>[1]) =>
      api.platform.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.user(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.users() });
    },
  });
};

/**
 * A tenant user's active device sessions, for the expandable row on the
 * platform Users page. Lazy: only fetched once `enabled` (the row is
 * expanded) so we don't fan out a request per user on list render.
 */
export const usePlatformUserSessions = (userId: string, enabled: boolean) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platform.tenantUserSessions(userId),
    queryFn: () => api.platform.listUserSessions(userId),
    enabled: enabled && Boolean(userId),
    staleTime: 30_000,
  });
};

/** Force-revoke a tenant user's session from the admin panel. */
export const useRevokePlatformSession = (userId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (sessionId: string) => api.platform.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.platform.tenantUserSessions(userId),
      });
    },
  });
};
